import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { usePrintStore } from '@renderer/stores/printStore'
import {
  processAndUploadStrip,
  processAndUploadPhotos,
  processAndUploadGIF,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import Button from '@renderer/components/bahela/Button'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { FrameConfig } from '@renderer/types/configState'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import cogoToast from '@successtar/cogo-toast'
import { FaQrcode } from 'react-icons/fa'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { resolveFramedVideoPlaybackAndRemote } from '@renderer/utils/galleryUrlUtils'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'

// Import Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaLogo from '/assets/bahela/bahela-green.webp'
import pickFrameId from '/assets/bahela/pick-frame-id.webp'
import pickFrameEn from '/assets/bahela/pick-frame-en.webp'
import loadingImage from '/assets/bahela/loading.webp'

interface UserContext {
  user?: {
    email: string
  }
}

const FramePage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { i18n } = useTranslation()
  // Auto-advance timeout is driven by the saved project config; 0 = no timeout.
  const [remainingTime, setRemainingTime] = useState<number>(
    () => useConfigStore.getState().getPageSettings('frame')?.frameTimeout || 0
  )
  const [selectedFrame, setSelectedFrame] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const previewRef = useRef<PrintPreviewRef>(null)
  const printJobSentRef = useRef<boolean>(false)
  const context = useOutletContext<UserContext | null>()

  const {
    id,
    setSessionState,
    gallery,
    galleryKey,
    userPreferences,
    logActivity
  } = useSessionStateStore()
  const [printGalleryQrOnStrip, setPrintGalleryQrOnStrip] = useState(
    () => userPreferences.printGalleryQrOnStrip !== false
  )
  const { config: globalConfig, getFrame, frames, getPageSettings } = useConfigStore()
  const { downloadLocal } = useSettingsStore()
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()

  const frameTimeout = getPageSettings('frame')?.frameTimeout || 0
  const frameCountdown = useCountdown(frameTimeout)
  const uploadStartedRef = useRef<boolean>(false)

  const sortedFrames = useMemo(() => {
    const pinId = userPreferences.frameId
    // Custom frames only appear when they are the voucher-linked frame for this session.
    const visibleFrames = frames.filter((f) => !f.isCustomFrame || f._id === pinId)
    if (!pinId || visibleFrames.length === 0) return visibleFrames
    const ix = visibleFrames.findIndex((f) => f._id === pinId)
    if (ix <= 0) return visibleFrames
    const next = [...visibleFrames]
    const [pinned] = next.splice(ix, 1)
    return [pinned, ...next]
  }, [frames, userPreferences.frameId])

  const validPhotos = gallery.capturedItems.filter((item) => item.photoUrl)
  const selectedFrameData = selectedFrame ? getFrame(selectedFrame) : null
  const maxSelectablePhotos = selectedFrameData?.drawParams.images
    ? new Set(
        selectedFrameData.drawParams.images
          .filter((image) => image.type.startsWith('photo'))
          .map((image) => image.type)
      ).size
    : 3

  const photosForStrip = validPhotos
    .slice(0, maxSelectablePhotos)
    .map((item) => item.photoUrl)

  const frameConfig = selectedFrameData
    ? {
        id: selectedFrameData._id,
        baseImage: selectedFrameData.thumbnail,
        drawParams: selectedFrameData.drawParams
      }
    : {
        id: 'preview',
        baseImage: '',
        drawParams: {
          canvasWidth: 213,
          canvasHeight: 652,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  useEffect((): (() => void) => {
    if (sortedFrames.length > 0) {
      const pinId = userPreferences.frameId
      const pinOk = pinId && sortedFrames.some((f) => f._id === pinId)
      setSelectedFrame((prev) => {
        if (pinOk && pinId) return pinId
        if (prev && sortedFrames.some((f) => f._id === prev)) return prev
        return sortedFrames[0]._id
      })
    }

    if (frameTimeout > 0) {
      frameCountdown.setOnTick(() => {
        setRemainingTime(frameCountdown.getRemainingSeconds())
      })

      frameCountdown.setOnComplete(() => {
        void handleContinue()
      })

      frameCountdown.start()
    }
    stateUploader.uploadSessionState()

    const initializeMedia = async (): Promise<void> => {
      if (uploadStartedRef.current) return
      uploadStartedRef.current = true

      const ctx: ProcessingContext = {
        context,
        projectId,
        sessionId: id || '',
        galleryId: galleryKey || gallery.id,
        downloadLocal,
        isOnline: true
      }

      try {
        const photoItems = validPhotos.map((item) => ({ photoUrl: item.photoUrl }))
        try {
          await Promise.race([
            processAndUploadPhotos(photoItems, ctx),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('Photo uploads timed out')), 15000)
            )
          ])
          const g = useSessionStateStore.getState().gallery
          setSessionState({
            gallery: {
              ...g,
              individualPhotosUploaded: true,
              individualPhotosUploadedAt: Date.now()
            }
          })
        } catch {
          console.warn('Photo uploads timed out, continuing with background uploads')
        }

        if (userPreferences.useGIF) {
          try {
            const photoUrls = validPhotos.map((item) => item.photoUrl)
            const gifSpeed = globalConfig?.pages?.capture?.settings?.gifSpeed || 1000
            const customGifFrame = globalConfig?.pages?.capture?.settings?.customGifFrame
            const { gifLocalUrl, mp4LocalUrl } = await processAndUploadGIF(
              photoUrls,
              gifSpeed,
              ctx,
              customGifFrame
            )
            const newItems: Array<{
              id: string
              animationUrl: string
              animationType: 'gif' | 'mp4'
              gifUrl: string
              photoUrl: string
              timestamp: Date
            }> = []
            if (mp4LocalUrl) {
              newItems.push({
                id: `dreambooth-animation-${gallery.id}`,
                animationUrl: mp4LocalUrl,
                animationType: 'mp4',
                gifUrl: '',
                photoUrl: '',
                timestamp: new Date()
              })
            }
            if (gifLocalUrl) {
              newItems.push({
                id: `dreambooth-animation-gif-${gallery.id}`,
                animationUrl: gifLocalUrl,
                animationType: 'gif',
                gifUrl: gifLocalUrl,
                photoUrl: '',
                timestamp: new Date()
              })
            }
            if (newItems.length > 0) {
              setSessionState({
                gallery: {
                  capturedItems: [...gallery.capturedItems, ...newItems]
                }
              })
            }
          } catch (err) {
            console.warn('GIF/MP4 creation/upload failed', err)
          }
        }
      } catch (err) {
        console.warn('[Frame] initializeMedia error:', err)
      }
    }
    initializeMedia()

    return () => {
      frameCountdown.stop()
    }
  }, [sortedFrames, userPreferences.frameId])

  const handleFrameClick = (frameId: string): void => {
    setSelectedFrame(frameId)
  }

  const handleContinue = async (): Promise<void> => {
    if (!selectedFrame || isProcessing) return

    const frame = frames.find((f) => f._id === selectedFrame)
    if (!frame) return

    const photoImage = frame.drawParams.images.find((img) => img.type.startsWith('photo-'))
    const photoRatio = photoImage ? photoImage.width / photoImage.height : null

    setSessionState({
      userPreferences: {
        ...userPreferences,
        frameId: selectedFrame,
        framePhotoRatio: photoRatio
      }
    })

    logActivity(`Selected frame: ${frame.name || selectedFrame}`, {
      frameId: selectedFrame
    })

    if (!previewRef.current || photosForStrip.length < maxSelectablePhotos) {
      cogoToast.error(
        i18n.language === 'id'
          ? 'Pratinjau tidak tersedia atau foto belum cukup.'
          : 'Preview not available or not enough photos.'
      )
      return
    }

    setIsProcessing(true)

    try {
      const stripBlob = await previewRef.current.exportImage()
      if (!stripBlob) throw new Error('Failed to export strip image')

      const localStripUrl = URL.createObjectURL(stripBlob)

      const stripCtx: ProcessingContext = {
        context,
        projectId,
        sessionId: id || '',
        galleryId: galleryKey || gallery.id,
        downloadLocal,
        isOnline: true
      }

      if (isPrintingEnabled && !printJobSentRef.current) {
        printJobSentRef.current = true
        const { printerName, settings } = getPrinterForFrame(selectedFrame)
        try {
          await initiatePrint(
            stripBlob,
            printerName,
            userPreferences.copies,
            settings,
            true,
            undefined,
            'session',
            // Ties the print-event row to this paid session (see bahela/Select.tsx).
            { sessionId: id || undefined, projectId: projectId || undefined }
          )
        } catch {
          // Logged in printService
        }
      }

      const stripResult = await processAndUploadStrip(stripBlob, stripCtx)
      const stripLocalUrl = stripResult?.localUrl ?? localStripUrl

      setSessionState({
        gallery: {
          capturedItems: [
            ...gallery.capturedItems,
            {
              id: `dreambooth-strip-${gallery.id}`,
              stripUrl: stripLocalUrl,
              photoUrl: '',
              timestamp: new Date()
            }
          ]
        }
      })

      // Framed video (strip_live / strip_story) when recording enabled - mirror view/Select
      if (
        userPreferences.useRecording &&
        ['recording', 'strip_live', 'strip_story'].includes(
          userPreferences.recordingType || ''
        )
      ) {
        const resources = photosForStrip.map((photoUrl) => {
          const item = gallery.capturedItems.find(
            (i) => i.photoUrl === photoUrl || i.originalPhotoUrl === photoUrl
          )
          if (!item) throw new Error(`Could not find captured item for photo ${photoUrl}`)
          const selectedFilterId = userPreferences.filterId
          const photoToUse =
            selectedFilterId && item.filteredVersions?.[selectedFilterId]
              ? item.filteredVersions[selectedFilterId]
              : item.photoUrl
          return {
            photoUrl: photoToUse,
            videoUrl: item.recordingUrl || ''
          }
        })
        const photoStates = previewRef.current?.getPhotoStates() || {}
        const unscaledFrame = getFrame(selectedFrame, 1)
        const unscaledFrameConfig: FrameConfig = unscaledFrame
          ? {
              id: unscaledFrame._id,
              baseImage: unscaledFrame.thumbnail,
              drawParams: unscaledFrame.drawParams
            }
          : {
              id: '',
              baseImage: '',
              drawParams: {
                canvasWidth: 1200,
                canvasHeight: 1800,
                backgroundColor: '#FFFFFF',
                images: []
              }
            }
        setSessionState({
          gallery: {
            ...gallery,
            liveModeProcessing: true,
            liveModeUrl: null,
            liveModeLocalUrl: undefined,
            liveModeRemoteUrl: undefined
          }
        })
        const taskId =
          userPreferences.recordingType === 'strip_story'
            ? `video-story-${id}-${Date.now()}`
            : `video-live-${id}-${Date.now()}`
        const captureCountdownSeconds =
          globalConfig?.pages?.capture?.settings?.captureCountdown || 3
        const captureCountdownMs = captureCountdownSeconds * 1000
        backgroundVideoProcessor.processVideo({
          id: taskId,
          type:
            userPreferences.recordingType === 'strip_story' ? 'story' : 'live',
          resources,
          frameConfig: unscaledFrameConfig,
          sessionId: id || '',
          userId: context?.user?.email ?? '',
          projectId: projectId ?? '',
          galleryId: galleryKey || gallery.id,
          speed: globalConfig?.pages?.capture?.settings?.recordingSpeed || 1,
          fps: 30,
          photoStates,
          countdown: captureCountdownMs,
          onComplete: (uploadedUrl: string, localUrl?: string) => {
            const { playbackUrl, videoRemoteUrl } = resolveFramedVideoPlaybackAndRemote(
              uploadedUrl,
              localUrl
            )
            const current = useSessionStateStore.getState().gallery
            setSessionState({
              gallery: {
                ...current,
                liveModeProcessing: false,
                liveModeUrl: playbackUrl || null,
                liveModeLocalUrl: localUrl,
                liveModeRemoteUrl: videoRemoteUrl
              }
            })
          },
          onError: (error) => {
            console.error('[Frame] Video processing error:', error)
            logActivity(`Video processing failed: ${error.message}`, {
              taskId,
              level: 'error'
            })
            const current = useSessionStateStore.getState().gallery
            setSessionState({
              gallery: {
                ...current,
                liveModeProcessing: false,
                liveModeUrl: null,
                liveModeLocalUrl: undefined,
                liveModeRemoteUrl: undefined
              }
            })
            cogoToast.error(
              i18n.language === 'id'
                ? 'Gagal memproses video'
                : 'Error processing video'
            )
          }
        })
      }

      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } catch (error) {
      console.error('Error generating strip:', error)
      cogoToast.error(
        i18n.language === 'id'
          ? 'Terjadi kesalahan dalam pemrosesan hasil sesi foto. Silakan laporkan masalah ini.'
          : 'An error occurred while processing your session. Please report this issue.'
      )
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } finally {
      setIsProcessing(false)
    }
  }

  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedPickFrame = useCachedImage(i18n.language === 'id' ? pickFrameId : pickFrameEn)
  const cachedLoadingImage = useCachedBackgroundImage(loadingImage)
  const cachedBahelaLogo = useCachedImage(bahelaLogo)

  return (
    <div className="relative bg-white w-full h-full overflow-hidden">

      {isProcessing && (
        <div
          className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
          style={{
            backgroundImage: `url(${cachedLoadingImage})`,
            backgroundSize: 'cover'
          }}
        />
      )}

      <div className="absolute right-[50px] top-[50px] flex flex-col items-center z-10">
        <button
          type="button"
          onClick={() => {
            if (isProcessing) return
            const next = !printGalleryQrOnStrip
            setPrintGalleryQrOnStrip(next)
            setSessionState({ userPreferences: { printGalleryQrOnStrip: next } })
            logActivity(`Gallery QR on strip ${next ? 'enabled' : 'disabled'}`)
          }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 shadow-lg ${
            printGalleryQrOnStrip
              ? 'bg-uriYellow text-methadone hover:bg-opacity-90'
              : 'bg-white text-methadone hover:bg-gray-100'
          } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={isProcessing}
        >
          <FaQrcode className="text-lg text-methadone" />
          <span className="font-body-bahela text-[20px] font-bold">
            {i18n.language === 'id' ? 'QR Galeri' : 'Gallery QR'}
          </span>
        </button>
      </div>

      <div className={'absolute w-[1036px] h-[963px] top-[93px] left-[267px]'}>
        <div className={'relative'}>
          <img alt="pick-frame" src={cachedPickFrame} className={'absolute top-0 left-[66px]'} />
        </div>
        <div
          className={
            'border-20 w-full mt-[100px] min-h-full rounded-[60px] bg-prominentBlue border-uriYellow flex flex-wrap justify-center items-center gap-12 px-4 pt-36'
          }
          style={{ backgroundImage: `url(${cachedBgLayer})` }}
        >
          <div className="grid grid-cols-2 gap-y-12 overflow-y-auto max-h-[800px] w-full pt-24 pb-48 px-24 custom-scrollbar">
            {sortedFrames.map((frame) => (
              <CachedFrameThumbnail
                key={frame._id}
                frame={frame}
                isSelected={selectedFrame === frame._id}
                onFrameClick={handleFrameClick}
              />
            ))}
          </div>
        </div>
      </div>

      <div
        className="bg-prominentBlue absolute top-0 right-0 py-[100px] px-[100px] h-full flex flex-col items-center justify-center gap-[30px]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      >
        <div className="relative">
          {selectedFrame && photosForStrip.length >= maxSelectablePhotos && (
            <PrintPreview
              ref={previewRef}
              frameConfig={frameConfig}
              selectedPhotos={photosForStrip}
              className="w-[320px] object-contain drop-shadow-2xl"
              sessionId={id || galleryKey || gallery.id}
              includeQrOnStrip={printGalleryQrOnStrip}
            />
          )}
          {selectedFrame && photosForStrip.length < maxSelectablePhotos && (
            <img
              crossOrigin="anonymous"
              alt="frame-preview"
              src={selectedFrameData?.thumbnail || ''}
              className="w-[320px] object-contain drop-shadow-2xl"
            />
          )}
        </div>
        {frameTimeout > 0 && (
          <div className="text-center text-white text-[50px] font-serif-bahela">
            {formatTime(remainingTime)}
          </div>
        )}
        <Button
          onClick={() => void handleContinue()}
          className={`text-[50px] ${
            !selectedFrame || photosForStrip.length < maxSelectablePhotos || isProcessing
              ? 'opacity-30'
              : 'opacity-100'
          }`}
          textColor="prominentBlue"
          disabled={
            !selectedFrame || photosForStrip.length < maxSelectablePhotos || isProcessing
          }
        >
          {i18n.language === 'id' ? 'Lanjut' : 'Next'}
        </Button>
      </div>

      <img
        src={cachedBahelaLogo}
        alt="Bahela logo"
        className="absolute z-[1] bottom-[66px] left-[57px] w-[127px] h-[114px]"
      />
    </div>
  )
}

const CachedFrameThumbnail: React.FC<{
  frame: { _id: string; name: string; thumbnail: string }
  isSelected: boolean
  onFrameClick: (frameId: string) => void
}> = ({ frame, isSelected, onFrameClick }) => {
  const cachedSrc = useCachedImage(frame.thumbnail)
  return (
    <div className="relative flex justify-center">
      <img
        crossOrigin="anonymous"
        alt={frame.name}
        src={cachedSrc}
        className={`object-contain cursor-pointer drop-shadow-2xl w-[300px] ${
          isSelected ? 'outline outline-8 outline-uriYellow rounded-[10px]' : ''
        }`}
        onClick={() => onFrameClick(frame._id)}
      />
    </div>
  )
}

export default FramePage
